import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader } from "@/components/layout/PageHeader";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useToast } from "@/contexts/ToastContext";
import { catalogApi, CreateDigitalResourceRequest } from "@/lib/api/catalog";
import type { Collection } from "@/lib/api/collections";
import { useCollections } from "@/hooks/useCollections";
import {
  Upload,
  FileText,
  X,
  Plus,
  Save,
  Video,
  Music,
  BookOpen,
  FileSpreadsheet,
  Globe,
  Lock,
  Download,
  AlertCircle,
  CheckCircle2,
  Layers,
  User,
  Tag,
  Calendar,
  Building,
  Hash,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { RequiredLabel } from "@/components/catalog/RequiredLabel";
import { isFieldRequiredOnPublish } from "@/lib/catalog/catalog-required-fields";
import { validateDigitalResourceForm } from "@/lib/catalog/catalog-validate-form";

type ResourceType =
  | "PDF"
  | "VIDEO"
  | "AUDIO"
  | "EBOOK"
  | "JOURNAL"
  | "RESEARCH_PAPER"
  | "INSTITUTIONAL_CONTENT"
  | "WEBSITE";

const resourceTypeConfig: Record<
  ResourceType,
  { label: string; icon: React.ElementType; color: string }
> = {
  PDF: { label: "PDF Document", icon: FileText, color: "text-red-500" },
  VIDEO: { label: "Video", icon: Video, color: "text-purple-500" },
  AUDIO: { label: "Audio", icon: Music, color: "text-green-500" },
  EBOOK: { label: "eBook", icon: BookOpen, color: "text-primary" },
  JOURNAL: { label: "Journal", icon: FileSpreadsheet, color: "text-orange-500" },
  RESEARCH_PAPER: { label: "Research Paper", icon: FileText, color: "text-teal-500" },
  INSTITUTIONAL_CONTENT: { label: "Institutional Content", icon: Building, color: "text-gray-500" },
  WEBSITE: { label: "Website / Online Resource", icon: Globe, color: "text-blue-500" },
};

export function UploadEResourcePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const { data: collectionsRaw = [], isLoading: loadingCollections } = useCollections(true);
  const collections = collectionsRaw.filter((c) => c.isActive);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  // Form data
  const [formData, setFormData] = useState<CreateDigitalResourceRequest>({
    title: "",
    description: "",
    authors: [],
    type: "PDF",
    publisher: "",
    publishedDate: "",
    subjects: [],
    tags: [],
    lcClassification: "",
    callNumber: "",
    collectionId: "",
    isPublic: false,
    allowDownload: false,
    websiteUrl: "",
  });

  // Input states for array fields
  const [authorInput, setAuthorInput] = useState("");
  const [subjectInput, setSubjectInput] = useState("");
  const [tagInput, setTagInput] = useState("");

  // Validation
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (file || formData.title || formData.authors?.length) {
      setHasUnsavedChanges(true);
    }
  }, [file, formData]);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelection(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelection = (selectedFile: File) => {
    setFile(selectedFile);

    // Auto-detect type from file extension
    const ext = selectedFile.name.split(".").pop()?.toLowerCase();
    if (["pdf"].includes(ext || "")) {
      setFormData((prev) => ({ ...prev, type: "PDF" }));
    } else if (["mp4", "avi", "mov", "wmv", "webm"].includes(ext || "")) {
      setFormData((prev) => ({ ...prev, type: "VIDEO" }));
    } else if (["mp3", "wav", "ogg", "m4a", "flac"].includes(ext || "")) {
      setFormData((prev) => ({ ...prev, type: "AUDIO" }));
    } else if (["epub", "mobi", "azw", "azw3"].includes(ext || "")) {
      setFormData((prev) => ({ ...prev, type: "EBOOK" }));
    }

    // Auto-fill title if empty
    if (!formData.title) {
      const titleWithoutExt = selectedFile.name.replace(/\.[^/.]+$/, "");
      setFormData((prev) => ({ ...prev, title: titleWithoutExt }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelection(e.target.files[0]);
    }
  };

  const addToArray = (
    field: "authors" | "subjects" | "tags",
    value: string,
    setter: (v: string) => void
  ) => {
    const trimmedValue = value.trim();
    if (trimmedValue && !formData[field]?.includes(trimmedValue)) {
      setFormData((prev) => ({
        ...prev,
        [field]: [...(prev[field] || []), trimmedValue],
      }));
      setter("");
    }
  };

  const removeFromArray = (field: "authors" | "subjects" | "tags", index: number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field]?.filter((_, i) => i !== index) || [],
    }));
  };

  const validateForm = (): boolean => {
    const newErrors = validateDigitalResourceForm({
      title: formData.title,
      type: formData.type,
      subjects: formData.subjects,
      hasFile: !!file,
      websiteUrl: formData.websiteUrl,
      mode: "publish",
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors before submitting.");
      return;
    }

    setLoading(true);
    try {
      const created = await catalogApi.uploadDigitalResource(file, formData);
      toast.success(`"${formData.title}" uploaded successfully!`);
      setHasUnsavedChanges(false);
      if (formData.type?.toUpperCase() === "PDF") {
        navigate(`/catalog/digital/${created.id}/edit`);
      } else {
        navigate("/catalog");
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to upload resource.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowDiscardDialog(true);
    } else {
      navigate("/catalog");
    }
  };

  const handleDiscard = () => {
    setHasUnsavedChanges(false);
    navigate("/catalog");
  };

  const getFileIcon = () => {
    const config = resourceTypeConfig[formData.type as ResourceType];
    const Icon = config?.icon || FileText;
    return <Icon className={cn("w-10 h-10", config?.color || "text-gray-400")} />;
  };

  if (loadingCollections) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <div className="mx-auto max-w-7xl">
          <PageHeader
            title="Upload E-Resource"
            description="Add digital content to your library collection"
            breadcrumbItems={[{ label: "Catalog" }, { label: "Upload E-Resource" }]}
            showSearch={false}
          />
          <div className="flex items-center justify-center py-24">
            <LoadingSpinner size="xl" label="Loading..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <div className="mx-auto max-w-7xl">
        <PageHeader
          title="Upload E-Resource"
          description="Add PDFs, videos, audio files, eBooks, and other digital content to your library"
          breadcrumbItems={[{ label: "Catalog" }, { label: "Upload E-Resource" }]}
          showSearch={false}
        />

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Main Form */}
            <div className="space-y-6 lg:col-span-2">
              {/* File Upload */}
              <Card className="overflow-hidden bg-white shadow-sm">
                <div className="p-6">
                  <h2 className="flex items-center gap-2 mb-4 text-lg font-semibold text-gray-900">
                    <Upload className="w-5 h-5 text-primary" />
                    File or Website URL
                  </h2>
                  <p className="mb-4 text-sm text-gray-600">
                    Upload a file, provide a website URL, or both. At least one is required.
                  </p>

                  <div
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={cn(
                      "relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all",
                      dragActive && "border-primary bg-primary/5 scale-[1.02]",
                      file && !errors.file && "border-green-500 bg-green-50",
                      errors.file && "border-red-500 bg-red-50",
                      !file &&
                        !dragActive &&
                        !errors.file &&
                        "border-gray-300 hover:border-gray-400"
                    )}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.mp4,.avi,.mov,.wmv,.webm,.mp3,.wav,.ogg,.m4a,.flac,.epub,.mobi,.azw,.azw3"
                      onChange={handleFileChange}
                      className="hidden"
                    />

                    {file ? (
                      <div className="flex flex-col items-center">
                        <div className="flex items-center justify-center w-20 h-20 mb-4 bg-white rounded-full shadow-sm">
                          {getFileIcon()}
                        </div>
                        <p className="text-lg font-medium text-gray-900">{file.name}</p>
                        <p className="text-sm text-gray-500">
                          {(file.size / (1024 * 1024)).toFixed(2)} MB •{" "}
                          {resourceTypeConfig[formData.type as ResourceType]?.label}
                        </p>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFile(null);
                          }}
                          className="mt-3 text-red-600 hover:text-red-700"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Remove File
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <div className="flex items-center justify-center w-20 h-20 mb-4 bg-gray-100 rounded-full">
                          <Upload className="w-10 h-10 text-gray-400" />
                        </div>
                        <p className="text-lg font-medium text-gray-900">Drop your file here</p>
                        <p className="mt-1 text-sm text-gray-500">
                          or click to browse • PDF, Video, Audio, eBook
                        </p>
                      </div>
                    )}
                  </div>

                  {errors.file && (
                    <p className="flex items-center gap-1 mt-2 text-sm text-red-600">
                      <AlertCircle className="w-4 h-4" />
                      {errors.file}
                    </p>
                  )}

                  <div className="mt-6 space-y-2">
                    <Label htmlFor="websiteUrl" className="flex items-center gap-1.5">
                      <Globe className="w-4 h-4 text-gray-400" />
                      Website URL
                    </Label>
                    <Input
                      id="websiteUrl"
                      type="url"
                      value={formData.websiteUrl || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, websiteUrl: e.target.value }))
                      }
                      placeholder="https://example.com/resource"
                      className={cn(errors.websiteUrl && "border-red-500")}
                    />
                    {errors.websiteUrl && (
                      <p className="flex items-center gap-1 text-sm text-red-600">
                        <AlertCircle className="w-4 h-4" />
                        {errors.websiteUrl}
                      </p>
                    )}
                    <p className="text-xs text-gray-500">
                      Use this when the resource is only available on an external website (no file
                      upload needed).
                    </p>
                  </div>
                </div>
              </Card>

              {/* Basic Information */}
              <Card className="overflow-hidden bg-white shadow-sm">
                <div className="p-6">
                  <h2 className="flex items-center gap-2 mb-4 text-lg font-semibold text-gray-900">
                    <FileText className="w-5 h-5 text-primary" />
                    Basic Information
                  </h2>

                  <div className="space-y-4">
                    <div>
                      <RequiredLabel
                        htmlFor="title"
                        required={isFieldRequiredOnPublish("E_RESOURCE", "title")}
                        error={errors.title}
                      >
                        Title
                      </RequiredLabel>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, title: e.target.value }))
                        }
                        placeholder="Enter resource title"
                        className={cn("mt-1.5", errors.title && "border-red-500")}
                      />
                      {errors.title && (
                        <p className="flex items-center gap-1 mt-1 text-sm text-red-600">
                          <AlertCircle className="w-4 h-4" />
                          {errors.title}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <RequiredLabel
                          htmlFor="type"
                          required={isFieldRequiredOnPublish("E_RESOURCE", "type")}
                          error={errors.type}
                        >
                          Resource Type
                        </RequiredLabel>
                        <Select
                          value={formData.type}
                          onValueChange={(value: any) =>
                            setFormData((prev) => ({ ...prev, type: value }))
                          }
                        >
                          <SelectTrigger className="mt-1.5">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(resourceTypeConfig).map(([key, config]) => {
                              const Icon = config.icon;
                              return (
                                <SelectItem key={key} value={key}>
                                  <span className="flex items-center gap-2">
                                    <Icon className={cn("w-4 h-4", config.color)} />
                                    {config.label}
                                  </span>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="collection" className="flex items-center gap-1.5">
                          <Layers className="w-4 h-4 text-gray-400" />
                          Collection
                        </Label>
                        <Select
                          value={formData.collectionId || "none"}
                          onValueChange={(value) =>
                            setFormData((prev) => ({
                              ...prev,
                              collectionId: value === "none" ? undefined : value,
                            }))
                          }
                        >
                          <SelectTrigger className="mt-1.5">
                            <SelectValue placeholder="Select collection (optional)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {collections.map((collection) => (
                              <SelectItem key={collection.id} value={collection.id}>
                                {collection.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, description: e.target.value }))
                        }
                        placeholder="Enter a description of this resource"
                        rows={3}
                        className="mt-1.5"
                      />
                    </div>
                  </div>
                </div>
              </Card>

              {/* Authors */}
              <Card className="overflow-hidden bg-white shadow-sm">
                <div className="p-6">
                  <h2 className="flex items-center gap-2 mb-4 text-lg font-semibold text-gray-900">
                    <User className="w-5 h-5 text-primary" />
                    Authors
                  </h2>

                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter author name and press Enter"
                      value={authorInput}
                      onChange={(e) => setAuthorInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addToArray("authors", authorInput, setAuthorInput);
                        }
                      }}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => addToArray("authors", authorInput, setAuthorInput)}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  {formData.authors && formData.authors.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {formData.authors.map((author, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm"
                        >
                          {author}
                          <button
                            type="button"
                            onClick={() => removeFromArray("authors", index)}
                            className="ml-1 hover:text-red-500"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Card>

              {/* Publication Details */}
              <Card className="overflow-hidden bg-white shadow-sm">
                <div className="p-6">
                  <h2 className="flex items-center gap-2 mb-4 text-lg font-semibold text-gray-900">
                    <Calendar className="w-5 h-5 text-primary" />
                    Publication Details
                  </h2>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <Label htmlFor="publisher" className="flex items-center gap-1.5">
                        <Building className="w-4 h-4 text-gray-400" />
                        Publisher
                      </Label>
                      <Input
                        id="publisher"
                        value={formData.publisher}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, publisher: e.target.value }))
                        }
                        placeholder="Enter publisher name"
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="publishedDate" className="flex items-center gap-1.5">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        Published Date
                      </Label>
                      <Input
                        id="publishedDate"
                        value={formData.publishedDate}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, publishedDate: e.target.value }))
                        }
                        placeholder="e.g., 2024 or 2024-01-15"
                        className="mt-1.5"
                      />
                    </div>
                  </div>
                </div>
              </Card>

              {/* Classification */}
              <Card className="overflow-hidden bg-white shadow-sm">
                <div className="p-6">
                  <h2 className="flex items-center gap-2 mb-4 text-lg font-semibold text-gray-900">
                    <Hash className="w-5 h-5 text-primary" />
                    Classification
                  </h2>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <Label htmlFor="lcClassification">LC Classification</Label>
                      <Input
                        id="lcClassification"
                        value={formData.lcClassification}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, lcClassification: e.target.value }))
                        }
                        placeholder="e.g., QA76.73"
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="callNumber">Call Number</Label>
                      <Input
                        id="callNumber"
                        value={formData.callNumber}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, callNumber: e.target.value }))
                        }
                        placeholder="e.g., QA76.73.J38 D67 2018"
                        className="mt-1.5"
                      />
                    </div>
                  </div>
                </div>
              </Card>

              {/* Subjects & Tags */}
              <Card className="overflow-hidden bg-white shadow-sm">
                <div className="p-6">
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div>
                      <RequiredLabel
                        required={isFieldRequiredOnPublish("E_RESOURCE", "subjects")}
                        error={errors.subjects}
                        className="flex items-center gap-2 mb-3 font-semibold text-gray-900"
                      >
                        <BookOpen className="w-4 h-4 text-gray-400" />
                        Subjects
                      </RequiredLabel>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Add subject"
                          value={subjectInput}
                          onChange={(e) => setSubjectInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              addToArray("subjects", subjectInput, setSubjectInput);
                            }
                          }}
                          className={cn("flex-1", errors.subjects && "border-red-500")}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addToArray("subjects", subjectInput, setSubjectInput)}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      {errors.subjects && (
                        <p className="flex items-center gap-1 mt-2 text-sm text-red-600">
                          <AlertCircle className="w-4 h-4" />
                          {errors.subjects}
                        </p>
                      )}
                      {formData.subjects && formData.subjects.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {formData.subjects.map((subject, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm"
                            >
                              {subject}
                              <button
                                type="button"
                                onClick={() => removeFromArray("subjects", index)}
                                className="ml-1 hover:text-red-500"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <h3 className="flex items-center gap-2 mb-3 font-semibold text-gray-900">
                        <Tag className="w-4 h-4 text-gray-400" />
                        Tags
                      </h3>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Add tag"
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              addToArray("tags", tagInput, setTagInput);
                            }
                          }}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addToArray("tags", tagInput, setTagInput)}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      {formData.tags && formData.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {formData.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-700 rounded text-sm"
                            >
                              <Tag className="w-3 h-3" />
                              {tag}
                              <button
                                type="button"
                                onClick={() => removeFromArray("tags", index)}
                                className="ml-1 hover:text-red-500"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              {/* Access Settings */}
              <Card className="sticky top-4 overflow-hidden bg-white shadow-sm">
                <div className="p-6">
                  <h3 className="flex items-center gap-2 mb-4 font-semibold text-gray-900">
                    <Lock className="w-4 h-4 text-gray-400" />
                    Access Settings
                  </h3>

                  <div className="space-y-4">
                    <label
                      htmlFor="isPublic"
                      className={cn(
                        "flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors",
                        formData.isPublic
                          ? "border-green-500 bg-green-50"
                          : "border-gray-200 hover:border-gray-300"
                      )}
                    >
                      <Checkbox
                        id="isPublic"
                        checked={formData.isPublic}
                        onCheckedChange={(checked) =>
                          setFormData((prev) => ({ ...prev, isPublic: !!checked }))
                        }
                      />
                      <div className="flex-1">
                        <span className="flex items-center gap-2 font-medium">
                          <Globe className="w-4 h-4 text-green-600" />
                          Public Access
                        </span>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Anyone can view this resource
                        </p>
                      </div>
                    </label>

                    <label
                      htmlFor="allowDownload"
                      className={cn(
                        "flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors",
                        formData.allowDownload
                          ? "border-primary bg-primary/5"
                          : "border-gray-200 hover:border-gray-300"
                      )}
                    >
                      <Checkbox
                        id="allowDownload"
                        checked={formData.allowDownload}
                        onCheckedChange={(checked) =>
                          setFormData((prev) => ({ ...prev, allowDownload: !!checked }))
                        }
                      />
                      <div className="flex-1">
                        <span className="flex items-center gap-2 font-medium">
                          <Download className="w-4 h-4 text-primary" />
                          Allow Downloads
                        </span>
                        <p className="text-xs text-gray-500 mt-0.5">Users can download this file</p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Upload Button */}
                <div className="p-6 border-t border-gray-100 bg-gray-50">
                  <Button
                    type="submit"
                    disabled={loading || !file}
                    size="lg"
                    className="w-full gap-2"
                  >
                    {loading ? (
                      <>
                        <span className="w-5 h-5 border-2 border-current rounded-full animate-spin border-t-transparent" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5" />
                        Upload Resource
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}
                    className="w-full mt-2"
                  >
                    Cancel
                  </Button>
                </div>
              </Card>

              {/* Help */}
              <Card className="p-4 bg-white shadow-sm">
                <h4 className="mb-2 font-medium text-gray-900">Supported Formats</h4>
                <ul className="space-y-1 text-xs text-gray-500">
                  <li>• PDF documents (.pdf)</li>
                  <li>• Video files (.mp4, .avi, .mov, .webm)</li>
                  <li>• Audio files (.mp3, .wav, .ogg, .m4a)</li>
                  <li>• eBooks (.epub, .mobi, .azw)</li>
                </ul>
              </Card>
            </div>
          </div>
        </form>
      </div>

      {/* Discard Confirmation */}
      <ConfirmDialog
        open={showDiscardDialog}
        onOpenChange={setShowDiscardDialog}
        title="Discard changes?"
        description="You have unsaved changes. Are you sure you want to leave this page?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        onConfirm={handleDiscard}
        variant="warning"
      />
    </div>
  );
}
